import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, Database, Clock, BarChart3, Sparkles, Download, Star, Play, Lock } from 'lucide-react';
import { DuckMark } from './DuckPilotLogo';
import duckdbService from '../services/duckdbService';
import dataProfileService from '../services/dataProfileService';
import nlpService from '../services/nlpService';
import exportService from '../services/exportService';
import queryHistoryStorage from '../services/queryHistoryStorage';
import usageService from '../services/usageService';

const QueryInterface = ({ selectedFile, onFileSelect, prefillQuestion, prefillToken, mode, isAuthenticated, onRequireAuth, onUsageUpdate }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);
  const [error, setError] = useState(null);
  const [schemaSuggestions, setSchemaSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [autoExportFormat, setAutoExportFormat] = useState('off');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Memoized queries - must be before any early returns
  const datasetHistory = useMemo(
    () => queryHistory.filter((item) => item.dataset_id === selectedFile?.file_id),
    [queryHistory, selectedFile]
  );

  const savedQueries = useMemo(
    () => datasetHistory.filter((item) => item.saved),
    [datasetHistory]
  );

  const recentQueries = useMemo(
    () => datasetHistory.filter((item) => !item.saved),
    [datasetHistory]
  );

  useEffect(() => {
    const storedFormat = localStorage.getItem('duckpilot_auto_export');
    if (storedFormat) {
      setAutoExportFormat(storedFormat);
    }
  }, []);

  useEffect(() => {
    let isActive = true;
    const loadHistory = async () => {
      try {
        const stored = await queryHistoryStorage.loadAllQueries();
        if (!isActive) return;
        stored.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setQueryHistory(stored);
      } catch (storageError) {
        console.warn('Failed to load query history:', storageError);
      }
    };
    loadHistory();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (prefillQuestion) {
      setQuestion(prefillQuestion);
      setError(null);
    }
  }, [prefillQuestion, prefillToken]);

  useEffect(() => {
    let isActive = true;

    const loadSuggestions = async () => {
      if (!selectedFile) return;
      setSuggestionsLoading(true);
      try {
        const suggestions = await dataProfileService.generateSchemaSuggestions(selectedFile.file_id);
        if (isActive) {
          setSchemaSuggestions(suggestions);
        }
      } catch (suggestionError) {
        console.warn('Failed to load schema suggestions:', suggestionError);
        if (isActive) {
          setSchemaSuggestions([]);
        }
      } finally {
        if (isActive) {
          setSuggestionsLoading(false);
        }
      }
    };

    loadSuggestions();
    return () => {
      isActive = false;
    };
  }, [selectedFile]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);
    const startTime = Date.now();

    try {
      if (mode !== 'demo') {
        if (!isAuthenticated) {
          setError('Create a free account to upload and query your own files.');
          if (onRequireAuth) onRequireAuth();
          setLoading(false);
          return;
        }
        await usageService.recordQuery();
        if (onUsageUpdate) onUsageUpdate();
      }

      const datasetInfo = duckdbService.getDatasetInfo(selectedFile.file_id);
      if (!datasetInfo) {
        throw new Error('Dataset not found. Please upload the file again.');
      }

      const sql = await nlpService.translateToSQL(
        question.trim(),
        datasetInfo.columns,
        datasetInfo.tableName
      );

      const queryResult = await duckdbService.executeQuery(sql, selectedFile.file_id);

      const answer = await nlpService.generateNaturalAnswer(
        question.trim(),
        sql,
        queryResult.result,
        queryResult.row_count
      );

      const endTime = Date.now();
      const clientTime = endTime - startTime;

      const result = {
        ...queryResult,
        question: question.trim(),
        timestamp: new Date().toISOString(),
        clientTime,
        answer
      };

      const historyItem = {
        id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        question: result.question,
        sql: result.sql,
        results: result.result || [],
        row_count: result.row_count || 0,
        execution_time_ms: result.execution_time_ms || 0,
        created_at: result.timestamp,
        dataset_id: selectedFile?.file_id || null,
        dataset_name: selectedFile?.filename || 'Unknown',
        saved: false
      };

      setResults(result);
      setPage(1);
      setQueryHistory(prev => [historyItem, ...prev].slice(0, 50));
      try {
        await queryHistoryStorage.saveQuery(historyItem);
      } catch (storageError) {
        console.warn('Failed to persist query history:', storageError);
      }

      if (autoExportFormat === 'csv') {
        exportService.exportToCSV(result.result || [], `query_${historyItem.id}`);
      }
      if (autoExportFormat === 'json') {
        exportService.exportQueryResults(result.result || [], result.question, {
          sql: result.sql,
          rowCount: result.row_count
        });
      }
      setQuestion('');
    } catch (error) {
      console.error('Query error:', error);
      const detail = error?.response?.data?.detail;
      const message = detail || (error?.message === 'auth_required'
        ? 'Create a free account to upload and query your own files.'
        : error?.message);
      setError(message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-white rounded-brand-md flex items-center justify-center mx-auto mb-4 border border-[rgba(32,30,29,0.12)] shadow-brand-sm">
            <Database className="w-8 h-8 text-sand-400" strokeWidth={2} />
          </div>
          <h3 className="font-display text-xl text-ink mb-2">No file selected</h3>
          <p className="text-sand-700">Please upload or select a file to start querying</p>
        </motion.div>
      </div>
    );
  }

  const handleToggleSaved = async (item) => {
    const nextSaved = !item.saved;
    setQueryHistory((prev) => prev.map((entry) => (
      entry.id === item.id ? { ...entry, saved: nextSaved } : entry
    )));
    try {
      await queryHistoryStorage.updateQuery(item.id, { saved: nextSaved });
    } catch (storageError) {
      console.warn('Failed to update query history:', storageError);
    }
  };

  const handleExportHistory = (item, format) => {
    if (!item.results || item.results.length === 0) {
      setError('No results available to export.');
      return;
    }
    const filename = `query_${item.dataset_name}_${item.id}`.replace(/\s+/g, '_').toLowerCase();
    if (format === 'json') {
      exportService.exportToJSON(item.results, filename);
      return;
    }
    if (format === 'excel') {
      exportService.exportToExcel(item.results, filename);
      return;
    }
    exportService.exportToCSV(item.results, filename);
  };

  const handleUseQuery = (item) => {
    setQuestion(item.question);
    setError(null);
  };

  const totalResults = results?.result?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, totalResults);
  const paginatedResults = results?.result?.slice(pageStart, pageEnd) || [];

  const handleAutoExportChange = (value) => {
    setAutoExportFormat(value);
    localStorage.setItem('duckpilot_auto_export', value);
  };

  const handleBatchExport = (format) => {
    if (!savedQueries.length) {
      setError('No saved queries to export.');
      return;
    }
    const filename = `saved_queries_${selectedFile?.filename || 'dataset'}`
      .replace(/\s+/g, '_')
      .toLowerCase();
    exportService.exportBatchQueries({
      title: 'DuckPilot Saved Queries',
      datasetName: selectedFile?.filename || null,
      queries: savedQueries,
      filename,
      format
    });
  };

  // Privacy indicator — approximate bytes the AI ever saw (schema + question + on-screen rows).
  const askColumns = (selectedFile?.columns || []).map((c) => (typeof c === 'string' ? c : c?.name || '')).filter(Boolean);
  const schemaBytes = askColumns.join(', ').length + askColumns.length * 6;
  const resultSampleBytes = results ? JSON.stringify((results.result || []).slice(0, 10)).length : 0;
  const sentBytes = schemaBytes + (results?.question?.length || question.length || 0) + resultSampleBytes;
  const formatSent = (n) => (n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`);

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* Privacy — a first-class surface, not a footnote */}
      <div className="flex justify-end">
        <span className="inline-flex items-center gap-2 rounded-full bg-sage-200 px-3.5 py-1.5 text-[11.5px] font-semibold text-sage-800 tnum">
          <Lock className="w-3 h-3" strokeWidth={2.75} />
          On this device · {formatSent(sentBytes)} sent to AI
        </span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-bill-100 rounded-brand-sm">
            <Sparkles className="w-5 h-5 text-bill-600" />
          </div>
          <h2 className="font-display text-xl text-ink">Ask a Question</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask anything about your data… e.g., 'Which region grew fastest quarter over quarter?'"
              className="w-full min-h-[100px] resize-none rounded-brand-md border border-sand-300 bg-sand-100 px-4 py-3 pr-14 text-ink placeholder:text-sand-500 outline-none transition-colors focus:border-bill-400 focus:bg-white"
              disabled={loading}
            />
            <motion.button
              type="submit"
              disabled={loading || !question.trim()}
              className="absolute bottom-3 right-3 grid place-items-center w-10 h-10 bg-bill-500 text-white rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-bill-600 transition-colors shadow-brand-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </motion.button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-sand-600">
            <span>Automation: auto-export results after each query</span>
            <select
              value={autoExportFormat}
              onChange={(event) => handleAutoExportChange(event.target.value)}
              className="bg-white border border-sand-300 rounded-brand-sm px-2 py-1 text-ink text-xs"
            >
              <option value="off">Off</option>
              <option value="csv">Export CSV</option>
              <option value="json">Export JSON</option>
            </select>
          </div>
        </form>
      </motion.div>

      {(schemaSuggestions.length > 0 || suggestionsLoading) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sage-100 rounded-brand-sm">
                <Sparkles className="w-5 h-5 text-sage-600" />
              </div>
              <h3 className="font-display text-lg text-ink">Suggested Questions</h3>
            </div>
            {suggestionsLoading && (
              <span className="text-xs text-sand-500">Loading…</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {schemaSuggestions.map((suggestion) => (
              <button
                key={suggestion.question}
                type="button"
                onClick={() => handleUseQuery({ question: suggestion.question })}
                className="text-left rounded-brand-md border border-[rgba(32,30,29,0.12)] bg-sand-100 px-4 py-3 hover:bg-white hover:border-bill-300 hover:shadow-brand-sm transition-all"
              >
                <div className="text-sm font-semibold text-ink">{suggestion.question}</div>
                <div className="text-xs text-sand-600 mt-1">{suggestion.description}</div>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="rounded-brand-lg border border-red-300 bg-red-50 p-6 shadow-brand-sm"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-brand-sm flex-shrink-0">
                <Database className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg text-red-700 mb-2">Query Error</h3>
                <div className="text-red-700/90 text-sm whitespace-pre-wrap mb-4">
                  {error}
                </div>
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setError(null)}
                    className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm rounded-full transition-colors border border-red-300"
                  >
                    Dismiss
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-4 py-2 bg-bill-500 text-white hover:bg-bill-600 text-sm rounded-full transition-colors disabled:opacity-50"
                  >
                    Try Again
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {results && results.sql && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >

            {/* Answer — the AI summary, written from the on-screen rows */}
            <div className="rounded-brand-lg bg-sage-100 border border-sage-300 p-5 flex gap-3 shadow-brand-sm">
              <DuckMark size={20} variant="sage" style={{ flex: 'none', marginTop: 2 }} />
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-sage-800 mb-1.5">Answer</div>
                <p className="text-[14.5px] leading-relaxed text-sage-900">{results.answer || 'No answer provided'}</p>
              </div>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-5 text-center shadow-brand-sm">
                <Clock className="w-7 h-7 text-sand-400 mx-auto mb-2" strokeWidth={2} />
                <div className="text-2xl font-bold text-ink tnum">{results.execution_time_ms || 0}<span className="text-base text-sand-500 ml-0.5">ms</span></div>
                <div className="text-sm text-sand-600">Ran locally in</div>
              </div>
              <div className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-5 text-center shadow-brand-sm">
                <Database className="w-7 h-7 text-sand-400 mx-auto mb-2" strokeWidth={2} />
                <div className="text-2xl font-bold text-ink tnum">{results.row_count || 0}</div>
                <div className="text-sm text-sand-600">Rows returned</div>
              </div>
              <div className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-5 text-center shadow-brand-sm">
                <BarChart3 className="w-7 h-7 text-sand-400 mx-auto mb-2" strokeWidth={2} />
                <div className="text-2xl font-bold text-ink tnum">{results.result && results.result.length > 0 ? Object.keys(results.result[0]).length : 0}</div>
                <div className="text-sm text-sand-600">Columns</div>
              </div>
            </div>


            <div className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-sand-200 rounded-brand-sm">
                  <Database className="w-5 h-5 text-sand-700" />
                </div>
                <h3 className="font-display text-lg text-ink">Generated SQL</h3>
              </div>
              <pre className="bg-ink p-4 rounded-brand-sm overflow-x-auto text-sm font-mono text-paper">
                {results.sql}
              </pre>
            </div>


            {results.result && results.result.length > 0 && (
              <div className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-sand-200 rounded-brand-sm">
                      <BarChart3 className="w-5 h-5 text-sand-700" />
                    </div>
                    <h3 className="font-display text-lg text-ink">Results</h3>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => exportService.exportToCSV(results.result, `query_${Date.now()}`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white hover:bg-sand-100 border border-sand-300 rounded-full text-sand-700 hover:text-ink transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      CSV
                    </button>
                    <button
                      onClick={() => exportService.exportToExcel(results.result, `query_${Date.now()}`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white hover:bg-sand-100 border border-sand-300 rounded-full text-sand-700 hover:text-ink transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Excel
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-brand-md border border-[rgba(32,30,29,0.12)]">
                  <table className="w-full tnum">
                    <thead className="bg-sand-100 border-b border-[rgba(32,30,29,0.12)]">
                      <tr>
                        {results.result && results.result[0] && Object.keys(results.result[0]).map((column) => (
                          <th key={column} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-sand-600">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sand-200">
                      {paginatedResults.map((row, index) => (
                        <tr key={index} className="hover:bg-sand-100 transition-colors">
                          {Object.values(row).map((value, cellIndex) => (
                            <td key={cellIndex} className="px-4 py-3 text-sm text-ink">
                              {value === null ? (
                                <span className="text-sand-400 italic">null</span>
                              ) : (
                                <span className="break-words">{String(value)}</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalResults > pageSize && (
                    <div className="px-4 py-3 bg-sand-100 text-sm text-sand-700 border-t border-[rgba(32,30,29,0.12)] flex flex-wrap items-center justify-between gap-3">
                      <span className="tnum">
                        Showing {pageStart + 1}-{pageEnd} of {totalResults} results
                      </span>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-xs text-sand-600">
                          Rows
                          <select
                            value={pageSize}
                            onChange={(event) => {
                              setPageSize(Number(event.target.value));
                              setPage(1);
                            }}
                            className="bg-white border border-sand-300 rounded-brand-sm px-2 py-1 text-ink text-xs"
                          >
                            {[10, 20, 50, 100].map((size) => (
                              <option key={size} value={size}>{size}</option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Prev
                        </button>
                        <span className="text-xs text-sand-600 tnum">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>


      {savedQueries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display text-lg text-ink">Saved Queries</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleBatchExport('json')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Export JSON
              </button>
              <button
                type="button"
                onClick={() => handleBatchExport('html')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Export HTML
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {savedQueries.slice(0, 5).map((query, index) => (
              <div
                key={query.id || index}
                className="p-3 bg-sand-100 rounded-brand-md border border-[rgba(32,30,29,0.12)]"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-sm text-ink flex-1">{query.question}</p>
                  <div className="flex items-center gap-2 text-xs text-sand-600 tnum">
                    <span>{query.execution_time_ms || 0}ms</span>
                    <span>•</span>
                    <span>{query.row_count || 0} rows</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleUseQuery(query)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bill-100 border border-bill-300 text-bill-700 hover:bg-bill-200 text-xs transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Use
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleSaved(query)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sage-200 text-sage-800 hover:bg-sage-300 text-xs transition-colors"
                  >
                    <Star className="w-3.5 h-3.5" />
                    Saved
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportHistory(query, 'csv')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportHistory(query, 'json')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    JSON
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportHistory(query, 'excel')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Excel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {recentQueries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm"
        >
          <h3 className="font-display text-lg text-ink mb-4">Recent Queries</h3>
          <div className="space-y-3">
            {recentQueries.slice(0, 5).map((query, index) => (
              <motion.div
                key={query.id || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-3 bg-sand-100 rounded-brand-md transition-colors border border-[rgba(32,30,29,0.12)]"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-sm text-ink flex-1">{query.question}</p>
                  <div className="flex items-center gap-2 text-xs text-sand-600 tnum">
                    <span>{query.execution_time_ms || 0}ms</span>
                    <span>•</span>
                    <span>{query.row_count || 0} rows</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleUseQuery(query)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bill-100 border border-bill-300 text-bill-700 hover:bg-bill-200 text-xs transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Use
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleSaved(query)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
                      query.saved
                        ? 'bg-sage-200 text-sage-800 hover:bg-sage-300'
                        : 'bg-white border border-sand-300 text-sand-700 hover:bg-sand-100'
                    }`}
                  >
                    <Star className="w-3.5 h-3.5" />
                    {query.saved ? 'Saved' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportHistory(query, 'csv')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportHistory(query, 'json')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    JSON
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportHistory(query, 'excel')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-sand-300 text-sand-700 hover:bg-sand-100 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Excel
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default QueryInterface; 
